/**
 * Texture formats the pipeline works with.
 *
 * Everything here is a *core* WebGPU storage-binding format, i.e. usable as
 * `texture_storage_2d<F, write>` without requesting optional features. That is a
 * hard constraint: the pipeline must run on a stock Chrome/Edge install with no
 * flags, on integrated GPUs included.
 *
 * - `rgba8unorm`   source images and exports
 * - `rgba16float`  working colour space (headroom for despill / unpremultiply)
 * - `r32float`     single channel maps (alpha, guide, paint overrides)
 * - `rg32float`    guided-filter (a, b) coefficients
 * - `rgba32float`  guided-filter moments (I, p, I*I, I*p)
 * - `r32uint`      connected component labels
 */
export type TexFormat =
  | 'rgba8unorm'
  | 'rgba16float'
  | 'r32float'
  | 'rg32float'
  | 'rgba32float'
  | 'r32uint';

/** Number of components each format carries. */
export const FORMAT_CHANNELS: Record<TexFormat, 1 | 2 | 4> = {
  rgba8unorm: 4,
  rgba16float: 4,
  r32float: 1,
  rg32float: 2,
  rgba32float: 4,
  r32uint: 1,
};

/** Bytes per texel, used for pool accounting and readback strides. */
export const FORMAT_BYTES: Record<TexFormat, number> = {
  rgba8unorm: 4,
  rgba16float: 8,
  r32float: 4,
  rg32float: 8,
  rgba32float: 16,
  r32uint: 4,
};

/**
 * WGSL texel type matching each format when bound as `texture_storage_2d`.
 * Used by the shader templating layer to stamp out format-polymorphic kernels.
 */
export const FORMAT_WGSL_TEXEL: Record<TexFormat, string> = {
  rgba8unorm: 'vec4f',
  rgba16float: 'vec4f',
  r32float: 'vec4f',
  rg32float: 'vec4f',
  rgba32float: 'vec4f',
  r32uint: 'vec4u',
};

/**
 * Sample type a bind group layout must declare for each format.
 *
 * This is why the package builds explicit bind group layouts instead of using
 * `layout: 'auto'`: WGSL spells every float texture `texture_2d<f32>`, which
 * auto-layout resolves to the *filterable* `'float'` sample type. Binding an
 * `r32float` / `rg32float` / `rgba32float` view - all of them unfilterable
 * without the optional `float32-filterable` feature - against that layout is a
 * validation error. Every kernel here reads with `textureLoad`, so declaring
 * `'unfilterable-float'` costs nothing and keeps the 32-bit intermediates legal.
 */
export const FORMAT_SAMPLE_TYPE: Record<TexFormat, GPUTextureSampleType> = {
  rgba8unorm: 'float',
  rgba16float: 'float',
  r32float: 'unfilterable-float',
  rg32float: 'unfilterable-float',
  rgba32float: 'unfilterable-float',
  r32uint: 'uint',
};

/** WGSL component type for a sampled binding of each format. */
export const FORMAT_WGSL_SAMPLED: Record<TexFormat, 'f32' | 'u32'> = {
  rgba8unorm: 'f32',
  rgba16float: 'f32',
  r32float: 'f32',
  rg32float: 'f32',
  rgba32float: 'f32',
  r32uint: 'u32',
};

/** Formats that WebGPU allows to be bound as `read_write` storage textures. */
export const FORMAT_READ_WRITE: ReadonlySet<TexFormat> = new Set<TexFormat>([
  'r32float',
  'r32uint',
]);

/** Tensor element types the GPU<->ONNX bridge can materialise. */
export type TensorDType = 'float32' | 'float16';

/** Layout of a 4D tensor in a flat buffer. */
export type TensorLayout = 'nchw' | 'nhwc';

/** Rectangle in pixel space. */
export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Anything `copyExternalImageToTexture` accepts as a source. */
export type ExternalImageSource =
  | ImageBitmap
  | HTMLCanvasElement
  | OffscreenCanvas
  | HTMLVideoElement
  | VideoFrame
  | HTMLImageElement;

/** How a kernel samples outside the source rectangle. */
export const EDGE_CLAMP = 0;
export const EDGE_PAD = 1;
export const EDGE_REFLECT = 2;
export type EdgeMode = typeof EDGE_CLAMP | typeof EDGE_PAD | typeof EDGE_REFLECT;

/** Resampling kernels understood by `resample.wgsl`. */
export const FILTER_BILINEAR = 0;
export const FILTER_LANCZOS3 = 1;
export const FILTER_AREA = 2;
export const FILTER_NEAREST = 3;
export type FilterMode =
  | typeof FILTER_BILINEAR
  | typeof FILTER_LANCZOS3
  | typeof FILTER_AREA
  | typeof FILTER_NEAREST;
